import React, { useCallback, useEffect, useReducer } from "react";
import ReactModal from "react-modal";
import { toast } from "react-toastify";
import { useAuth0 } from "@auth0/auth0-react";
import NumberFormat from "react-number-format";
import "@mobiscroll/react/dist/css/mobiscroll.min.css";

import ai from "../../helpers/axios";
import { LoadingButton } from "../shared/loading-button";
import TextAreaInput from "../textarea-input";
import TextInput from "../text-input";
import shortenName from "../../helpers/shorten-name";
import getApiErrorMessage from "../../helpers/get-api-error-message";

const toastOptions = {
  position: "top-right",
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
};

const reducer = (state, action) => {
  switch (action.type) {
    case "fieldChanged": {
      return {
        ...state,
        [action.field]: action.value,
      };
    }

    case "openModal": {
      return {
        ...state,
        showModal: true,
        ...(action.payload || {}),
      };
    }

    case "openDeleteModal": {
      return {
        ...state,
        showDeleteModal: true,
        ...(action.payload || {}),
      };
    }

    case "closeModal": {
      return {
        ...state,
        showModal: false,
        showDeleteModal: false,
        itemId: null,
        amount: null,
        paidTo: "",
        notes: "",
      };
    }

    default:
      break;
  }
  return state;
};

const ReferralFeeModule = (props) => {
  const initialState = {
    showModal: false,
    showDeleteModal: false,
    saving: false,
    data: props.data || [],
    loading: false,
    itemId: null,
    amount: null,
    paidTo: "",
    notes: "",
  };

  const { getAccessTokenSilently } = useAuth0();
  const { tripId, parentDispatch, dataDownloadedTimestamp, readOnly } = props;

  const [state, dispatch] = useReducer(reducer, initialState);

  const handleAddItem = useCallback((event) => {
    event.preventDefault();

    dispatch({
      type: "openModal",
    });
  }, []);

  const handleEditItem = useCallback((event, item) => {
    event.preventDefault();

    dispatch({
      type: "openModal",
      payload: {
        itemId: item._id,
        amount: item.amount,
        paidTo: item.paidTo,
        notes: item.notes,
      },
    });
  }, []);

  const handleDeleteItem = useCallback((event, item) => {
    event.preventDefault();

    dispatch({
      type: "openDeleteModal",
      payload: {
        itemId: item._id,
      },
    });
  }, []);

  const handleCloseModal = useCallback(() => {
    dispatch({
      type: "closeModal",
    });
  }, []);

  const handleDeleteItemConfirm = useCallback(async (event) => {
    event.preventDefault();

    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: true,
    });

    try {
      const token = await getAccessTokenSilently();
      const response = await ai.auth(token).delete(
        `/api/trips/${tripId}/referral-fees/${state.itemId}`,
        {},
      );

      if (response) {
        handleCloseModal();

        parentDispatch({
          type: "fieldChanged",
          field: "refreshDataToken",
          value: Date.now(),
        });
      }
    } finally {
      dispatch({
        type: "fieldChanged",
        field: "saving",
        value: false,
      });
    }
  }, [getAccessTokenSilently, handleCloseModal, tripId, parentDispatch, state.itemId]);

  const handleFormSubmit = useCallback(async (event) => {
    event.preventDefault();

    if ((!state.amount && state.amount !== 0) || !state.paidTo) {
      return toast.error("Please fill in all required fields and retry.", toastOptions);
    }

    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: true,
    });

    try {
      const token = await getAccessTokenSilently();
      let response;
      if (state.itemId) {
        response = await ai.auth(token).put(
          `/api/trips/${tripId}/referral-fees/${state.itemId}`,
          {
            amount: state.amount,
            paidTo: state.paidTo,
            notes: state.notes,
          },
        );
      } else {
        response = await ai.auth(token).post(
          `/api/trips/${tripId}/referral-fees`,
          {
            amount: state.amount,
            paidTo: state.paidTo,
            notes: state.notes,
          },
        );
      }

      if (response) {
        handleCloseModal();

        parentDispatch({
          type: "fieldChanged",
          field: "refreshDataToken",
          value: Date.now(),
        });
      }
    } catch (error) {
      const message = getApiErrorMessage(error, "");
      if (message) {
        toast.error(message);
      }
    } finally {
      dispatch({
        type: "fieldChanged",
        field: "saving",
        value: false,
      });
    }
  }, [getAccessTokenSilently, handleCloseModal, tripId, parentDispatch, state.itemId, state.amount, state.paidTo, state.notes]);

  return (
    <>
      <div
        className="trip-notes-module"
        key={`key-referral-fee-${dataDownloadedTimestamp.toString()}`}
        id="trip-referral-fee"
      >
        <h5 className="mb-1 d-flex">
          Referral Fees

          {!readOnly && (
            <button
              className="base-button icon-button icon-button-small evo-dark-green ml-2"
              onClick={handleAddItem}
              title="Add"
            >
              <span className="material-icons">add_circle</span>
            </button>
          )}
        </h5>

        <div className="trip-notes-table trip-referral-fee-table pb-0">
          <table>
            <thead className="fm-l2-parent-table-head">
            <tr className="fm-l2-parent-table-head-row">
              <th>Amount</th>
              <th>Paid to</th>
              <th>Notes</th>
              <th>Entered By</th>
              <th className="text-align-right">Actions</th>
            </tr>
            </thead>
            <tbody className="fm-l2-parent-table-body">
            {!state.data?.length && (
              <tr className="fm-l2-parent-table-row">
                <td className="text-align-left" colSpan={5}>No referral fees have been added</td>
              </tr>
            )}
            {state.data?.map((item) => (
              <tr className="fm-l2-parent-table-row">
                <td>
                  {item.amount.toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                    maximumFractionDigits: 0,
                  })}
                </td>
                <td>{item.paidTo}</td>
                <td>{item.notes}</td>
                <td>{shortenName(item?.enteredBy?.name)}</td>
                <td>
                  {!readOnly && (
                    <div className="d-flex justify-content-end">
                      <button
                        className="base-button icon-button icon-button-small evo-blue"
                        onClick={(event) => handleEditItem(event, item)}
                        title="Edit"
                      >
                        <span className="material-icons">edit</span>
                      </button>

                      <button
                        className="base-button icon-button icon-button-small btn-danger ml-2"
                        onClick={(event) => handleDeleteItem(event, item)}
                        title="Delete"
                      >
                        <span className="material-icons">delete</span>
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            </tbody>
          </table>
        </div>
      </div>

      <ReactModal
        isOpen={state.showModal}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={handleCloseModal}
        className="trip-notes-module-modal"
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            border: "none",
            maxWidth: "350px",
            maxHeight: "80vh",
            minWidth: "calc(min(350px, 95vw))",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header edit-note-modal">
          <h4>{state.itemId ? "Edit" : "Add"} Referral Fee</h4>
        </div>
        <div className="modal-body edit-note-modal">
          <form onSubmit={handleFormSubmit} className="w-100 base-form mt-20">
            <div className="form-group">
              <label htmlFor={`amount`} className="base-label">
                Amount <span className="required">*</span>
              </label>

              <NumberFormat
                value={state.amount}
                thousandSeparator={true}
                prefix={"$"}
                onValueChange={(values) => {
                  dispatch({
                    type: "fieldChanged",
                    field: "amount",
                    value: values.value,
                    fieldType: "float",
                  });
                }}
                placeholder="Amount"
                className="semibold-text"
                decimalScale={0}
                name="amount"
                allowNegative={false}
              />
            </div>

            <TextInput
              name="paidTo"
              label="Paid To"
              handleChange={(e) => {
                dispatch({
                  type: "fieldChanged",
                  field: "paidTo",
                  value: e.target.value,
                });
              }}
              value={state.paidTo}
              type="text"
              required
            />

            <TextAreaInput
              label="Notes"
              name="notes"
              value={state.notes}
              handleChange={(e) => {
                dispatch({
                  type: "fieldChanged",
                  field: "notes",
                  value: e.target.value,
                });
              }}
            />

            <div className="w-100 mt-20 d-flex gap-20">
              <LoadingButton
                className="base-button evo-blue flex-half"
                type="submit"
                disabled={state.saving}
              >
                Submit
              </LoadingButton>

              <button
                className="base-button btn-danger flex-half"
                type="button"
                onClick={handleCloseModal}
                disabled={state.saving}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </ReactModal>

      <ReactModal
        isOpen={state.showDeleteModal}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={handleCloseModal}
        className="trip-notes-module-modal"
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            padding: "40px",
            border: "none",
            maxWidth: "700px",
            maxHeight: "50%",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header base-modal">
          <h4>Delete Referral Fee</h4>
        </div>

        <div className="modal-body base-modal overflow-scroll">
          <div className="w-100 mt-20">
            This referral fee will be permanently deleted from the trip. Are you sure you want to continue?
          </div>

          <div className="w-100 mt-20 d-flex gap-20">
            <button
              className="base-button evo-blue flex-half"
              type="button"
              onClick={handleCloseModal}
              disabled={state.saving}
            >
              Cancel
            </button>

            <LoadingButton
              className="base-button btn-danger flex-half"
              type="button"
              onClick={handleDeleteItemConfirm}
              isLoading={state.saving}
            >
              Delete
            </LoadingButton>
          </div>
        </div>
      </ReactModal>
    </>
  );
};

export default ReferralFeeModule;
